import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { useHistory, useLocation } from 'react-router-dom';
import useStyles from '../../../assets/styles/DQProV2SignupStyles';
import PoweredByDQ_Default from '../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQ_White from '../../../assets/images/PoweredByDQ_white.png';
import { useColorMode } from '../../../context/colorMode/colorMode.context';
import { getGoogleSignInLink, loginUserViaToken, persistGuestRegisterUser, persistGuestGetGoogleSignInLink } from '../../../services/auth.service';
import { useAuth } from '../../../context/auth';
import { save_e2ee_keys } from '../../../utils/utils';
import { LOCAL_STORAGE_KEYS } from '../../../utils/constants';
import LoaderWithBackDrop from '../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import PasswordWithValidation, { getPasswordChecks } from './components/PasswordWithValidationsField.comp';
import SignupApplicationSteps from './components/SignupApplicationSteps.comp';
import { getSignupCatalogService } from '../../../services/pbdqSignup.service';
import { readHandover } from '../PbdqSignup/signup.session';

const getPostLoginRedirect = (user, userMessage) => {
    // if (user?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE) {
    //     return ['/my_workspace', undefined];
    // }
    return ['/my_workspace', { userMessage }];
};

const DQProV2Signup = () => {
    const classes = useStyles();
    const { isDark } = useColorMode();
    const PoweredByDQ_Logo = isDark ? PoweredByDQ_White : PoweredByDQ_Default;
    const history = useHistory();
    const location = useLocation();
    const { setAuthToken, setAuthUser, authUser, authToken } = useAuth();

    // Persist userMessage in localStorage to survive Google redirect
    const [userMessage, setUserMessage] = useState(location.state?.userMessage || localStorage.getItem(LOCAL_STORAGE_KEYS.dqpro_user_message) || '');

    // Referer passed when navigating from login (carries the original protected route)
    const referer = location.referer || location.state?.referer;
    const postLoginPath = referer?.pathname && referer.pathname !== '/login' && referer.pathname !== '/signup'
        ? referer.pathname
        : null;

    useEffect(() => {
        if (location.state?.userMessage) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.dqpro_user_message, location.state.userMessage);
            setUserMessage(location.state.userMessage);
        }
    }, [location.state?.userMessage]);

    // Redirect already-authenticated (non-guest) users away from the signup page
    useEffect(() => {
        if (authToken && authUser && !authUser.is_guest) {
            history.replace(...getPostLoginRedirect(authUser, userMessage));
        }
    }, [authToken, authUser]); // eslint-disable-line react-hooks/exhaustive-deps

    console.log('[DQProV2Signup] userMessage:', userMessage);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    // Optional name fields — shown when converting a guest session
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    /*
     * The sign-up is the Powered by DQ chat's continuation (13–14 Oct 2026): it opens
     * on the chat's own next steps — "Your information" first, where the email is
     * entered — with no email and password step. No account is created here: an
     * application goes to pbdq_applications, as in the chat, and its account gets a
     * generated password. `handover` is what the chat collected before sending the
     * visitor here; with none (the page opened directly) the details go to an admin.
     *
     * Only a guest saving their work still uses the email and password form below,
     * which creates their account at once (persistGuestRegisterUser).
     */
    const [handover] = useState(readHandover);
    const [signupCatalog, setSignupCatalog] = useState(null);
    /*
     * Back to this page from a card payment's redirect (?ref=&dqt_checkout=) or the
     * DQ Pro payment link (?ref=&pay=1&t=): straight to the application, as the chat
     * does on its own page.
     */
    const [link] = useState(() => {
        const params = new URLSearchParams(location.search);
        const ref = params.get('ref');
        const sessionId = params.get('dqt_checkout');
        const pay = params.get('pay') === '1';
        return ref && (sessionId || pay) ? { ref, sessionId, pay, token: params.get('t') } : null;
    });
    useEffect(() => {
        getSignupCatalogService().then((res) => setSignupCatalog(res?.data || null)).catch(() => {});
    }, []);
    const [errors, setErrors] = useState({
        email: '',
        password: '',
        confirmPassword: ''
    });

    const loginViaToken = async (token) => {
        try {
            setLoading(true);
            const result = await loginUserViaToken(token, false);
            if (result.data && result.data.user) {
                setAuthToken(result.data.auth_token);
                setAuthUser(result.data.user);
                localStorage.setItem(LOCAL_STORAGE_KEYS.refresh_token, JSON.stringify(result.data.refresh_token));
                localStorage.setItem(LOCAL_STORAGE_KEYS.user_email, JSON.stringify(result.data.user.email));
                if (result.data.e2ee_pub_key) save_e2ee_keys(result);
                const savedReferer = localStorage.getItem('dqpro_v2_login_referer');
                if (savedReferer) {
                    localStorage.removeItem('dqpro_v2_login_referer');
                    history.push(savedReferer);
                } else {
                    history.push(...getPostLoginRedirect(result.data.user, userMessage));
                }
            }
        } catch (e) {
            // error already toasted by invokeApi
        } finally {
            setLoading(false);
        }
    };

    const loginWithGoogleAction = async () => {
        setLoading(true);
        try {
            // Guest session: use the persist endpoint so the backend can link the
            // Google account to the existing guest session (no warning needed —
            // registering a new account preserves guest data).
            const result = authUser?.is_guest
                ? await persistGuestGetGoogleSignInLink()
                : await getGoogleSignInLink();
            localStorage.setItem(LOCAL_STORAGE_KEYS.refresh_token, JSON.stringify(result.data.refresh_token));
            localStorage.setItem(LOCAL_STORAGE_KEYS.user_email, JSON.stringify(result.data.user.email));
            localStorage.setItem(LOCAL_STORAGE_KEYS.dqpro_v2_google_auth_redirect, 'true');
            if (postLoginPath) localStorage.setItem('dqpro_v2_login_referer', postLoginPath);
            const { redirect_link } = result.data;
            window.open(redirect_link, '_self');
            // Do NOT setLoading(false) — page navigates away
        } catch (e) {
            // error already toasted by invokeApi
            setLoading(false);
        }
    };

    // Handle Google OAuth2 redirect callback on mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const access_token = params.get('otp_token');
        if (access_token) {
            loginViaToken(access_token).then(r => console.log('Logged in via Google callback token'));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleContinue = async () => {
        // Step 2: the details are checked, then the account is created below.
        let newErrors = { email: '', password: '', confirmPassword: '' };
        let hasError = false;

        if (!email.trim()) {
            newErrors.email = 'Please enter your email address';
            hasError = true;
        }
        if (!password.trim()) {
            newErrors.password = 'Please enter a password';
            hasError = true;
        } else {
            const checks = getPasswordChecks(password);
            const isPasswordValid = Object.values(checks).every(Boolean);
            if (!isPasswordValid) {
                newErrors.password = 'Password must include 8+ chars, upper, lower, number, and special character.';
                hasError = true;
            }
        }
        if (password !== confirmPassword) {
            newErrors.confirmPassword = "The Confirm Password confirmation doesnt match.";
            hasError = true;
        }

        setErrors(newErrors);
        if (hasError) return;

        try {
            setLoading(true);

            // ── Guest persist flow ────────────────────────────────────────────
            if (authUser?.is_guest) {
                const result = await persistGuestRegisterUser(
                    email.trim(),
                    password,
                    firstName.trim() || undefined,
                    lastName.trim() || undefined
                );
                if (result.data && result.data.user) {
                    setAuthToken(result.data.auth_token);
                    setAuthUser(result.data.user);
                    if (result.data.refresh_token) {
                        localStorage.setItem(LOCAL_STORAGE_KEYS.refresh_token, JSON.stringify(result.data.refresh_token));
                    }
                    localStorage.setItem(LOCAL_STORAGE_KEYS.user_email, JSON.stringify(result.data.user.email));
                    if (result.data.e2ee_pub_key) save_e2ee_keys(result);
                    if (postLoginPath) {
                        history.push(postLoginPath);
                    } else {
                        history.push(...getPostLoginRedirect(result.data.user, userMessage));
                    }
                }
                return;
            }

        } catch (e) {
            // error already toasted by invokeApi
        } finally {
            setLoading(false);
        }
    };

    // Everyone but a guest saving their work goes straight to the chat's steps.
    if (link || !authUser?.is_guest) {
        return (
            <Box className={classes.loginPage}>
                <Box sx={{ width: '100%', maxWidth: 640, px: 2, py: 5, mx: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <img src={PoweredByDQ_Logo} alt="Powered by DQ" className={classes.logo} style={{ cursor: 'pointer' }}
                        onClick={() => history.push('/my_workspace')}/>
                    <Box sx={{ width: '100%', mt: 2, p: { xs: 2.5, sm: 4 }, borderRadius: '16px', backgroundColor: 'var(--brand-surface, #fff)', border: '1px solid var(--brand-border, #E5E7EB)' }}>
                        <SignupApplicationSteps
                            handover={handover}
                            link={link}
                            catalog={signupCatalog}
                            // Back to the chat that handed over; nothing to go back to otherwise.
                            onBackFromFirst={handover ? () => history.goBack() : undefined}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box className={classes.loginPage}>
            <Box className={classes.container}>
                <Typography className={classes.welcomeText}>Welcome to</Typography>
                <img 
                    src={PoweredByDQ_Logo} 
                    alt="Powered by DQ" 
                    className={classes.logo} 
                    style={{ cursor: 'pointer' }}
                    onClick={() => history.push('/my_workspace')}
                />
                {authUser?.is_guest && (
                    <Typography style={{ fontSize: '15px', color: 'var(--brand-text-muted, #555)', marginBottom: '16px', textAlign: 'center', fontFamily: "'Roboto', sans-serif" }}>
                        Create an account to save your program.
                    </Typography>
                )}

                {process.env.REACT_APP_ENABLE_LOGIN_WITH_GOOGLE === '1' && (
                    <Button className={classes.googleButton} onClick={loginWithGoogleAction} disabled={loading}>
                        <img
                            src="https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png"
                            alt="Google Icon"
                            style={{ width: '20px', height: '20px' }}
                        />
                        Sign up with Google
                    </Button>
                )}

                <Box className={classes.dividerContainer}>
                    <Box className={classes.dividerLine}></Box>
                    <Box className={classes.dividerText}>OR</Box>
                    <Box className={classes.dividerLine}></Box>
                </Box>

                <form className={classes.form} onSubmit={(e) => { e.preventDefault(); handleContinue(); }}>
                    <Typography className={classes.label}>Email</Typography>
                    <input
                        className={classes.input}
                        type="email"
                        name="email"
                        autoComplete="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                    />
                    {errors.email && (
                        <Typography style={{ color: '#d32f2f', fontSize: '13px', marginBottom: '10px', marginTop: '-8px', textAlign: 'left', width: '100%', fontWeight: '400' }}>
                            {errors.email}
                        </Typography>
                    )}
                    <Typography className={classes.label}>Password</Typography>
                    <PasswordWithValidation
                        value={password}
                        onChange={setPassword}
                        placeholder="Enter your password"
                        name="password"
                        disabled={loading}
                        inputClassName={classes.input}
                    />
                    {errors.password && (
                        <Typography style={{ color: '#d32f2f', fontSize: '13px', marginBottom: '10px', marginTop: '-8px', textAlign: 'left', width: '100%', fontWeight: '400' }}>
                            {errors.password}
                        </Typography>
                    )}
                    <Typography className={classes.label}>Confirm Password</Typography>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        <input
                            className={classes.input}
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="confirm_password"
                            autoComplete="new-password"
                            placeholder="Re-enter your password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={loading}
                            style={{ paddingRight: '42px' }}
                        />
                        <IconButton
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                            disabled={loading}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                borderRadius: '8px',
                                p: 0.35,
                                color: 'var(--brand-text-muted, #4d4d4d)',
                            }}
                        >
                            {showConfirmPassword ? <VisibilityOff style={{ fontSize: 16 }} /> : <Visibility style={{ fontSize: 16 }} />}
                        </IconButton>
                    </Box>
                    {errors.confirmPassword && (
                        <Typography style={{ color: '#d32f2f', fontSize: '13px', marginBottom: '10px', marginTop: '-8px', textAlign: 'left', width: '100%', fontWeight: '400' }}>
                            {errors.confirmPassword}
                        </Typography>
                    )}
                    <Button
                        className={`${classes.continueButton} ${(!email.trim() || !password.trim() || !confirmPassword.trim() || (password !== confirmPassword)) ? '' : classes.continueButtonActive}`}
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? 'Please wait...' : 'Continue'}
                    </Button>
                </form>

                <Typography className={classes.signupText}>
                    Already have an account? <span className={classes.signupLink} onClick={() => history.push({ pathname: '/login', referer: location.referer }, { userMessage })}>Log in</span>
                </Typography>
            </Box>

            <Box className={classes.footerContainer}>
                <Typography className={classes.footerText}>
                    <span className={classes.footerLink}>Terms of Service</span> and <span className={classes.footerLink}>Privacy Policy</span>.
                </Typography>
            </Box>
            <LoaderWithBackDrop loading={loading} />
        </Box>
    );
};

export default DQProV2Signup;
